import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, InputAdornment, ToggleButtonGroup, ToggleButton, FormControl, InputLabel, Select, Checkbox, ListItemText, Menu } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Refresh, CheckCircle, Search } from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { filingApi } from '../services/api';
import { FilingRecord, FilingStatus } from '../types';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';


const Filings: React.FC = () => {
  const [filings, setFilings] = useState<FilingRecord[]>([]);
  const [filteredFilings, setFilteredFilings] = useState<FilingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFiling, setSelectedFiling] = useState<FilingRecord | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<FilingStatus>(FilingStatus.PENDING);
  const [notes, setNotes] = useState('');
  const [statusDate, setStatusDate] = useState('');
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('fi_search') || '');
  const [statusFilter, setStatusFilter] = useState<string[]>(() => { try { const s = localStorage.getItem('fi_status'); return s ? JSON.parse(s) : []; } catch { return []; } }); 
  const [employeeFilter, setEmployeeFilter] = useState<string[]>(() => { try { const s = localStorage.getItem('fi_employee'); return s ? JSON.parse(s) : []; } catch { return []; } });
  const [frequencyFilter, setFrequencyFilter] = useState<string[]>(() => { try { const s = localStorage.getItem('fi_frequency'); return s ? JSON.parse(s) : []; } catch { return []; } });
  const [dueByDate, setDueByDate] = useState<Date | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'needs-attention'>('all');
  const [sentToClientFilter, setSentToClientFilter] = useState<string>(() => localStorage.getItem('fi_sent') || 'all');
  const [selectedRows, setSelectedRows] = useState<string[]>([]); 
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const location = useLocation();
  const navigate = useNavigate();
  const [highlightedFilingId, setHighlightedFilingId] = useState<string | null>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canModifyFilings = user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';
const [dynamicColumns, setDynamicColumns] = useState<GridColDef[]>([]);
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const statusParam = searchParams.get('status');
    const employeeParam = searchParams.get('employee');
    const filingIdParam = searchParams.get('filingId');
    
    if (filingIdParam) {
      // When navigating to a specific filing, clear all filters to ensure it's visible
      setHighlightedFilingId(filingIdParam);
      setSearchTerm('');
      setStatusFilter([]);
      setEmployeeFilter([]);
      setFrequencyFilter([]);
      setDueByDate(null);
      setViewMode('all');
    setSentToClientFilter('all');
    } else {
      // Only apply URL params if not navigating to specific filing
      if (statusParam && ['PENDING', 'OVERDUE', 'WORK_IN_PROGRESS', 'RECEIVED', 'FILED', 'INACTIVE'].includes(statusParam)) {
        setStatusFilter([statusParam as FilingStatus]);
      }
      
      if (employeeParam) {
        setEmployeeFilter([employeeParam]);
      }
    }
  }, [location.search]);

  // Separate useEffect to handle pagination navigation after filters are applied
  useEffect(() => {
    if (highlightedFilingId && filteredFilings.length > 0) {
      // Find the index of the highlighted filing
      const filingIndex = filteredFilings.findIndex(f => f.id === highlightedFilingId);
      
      if (filingIndex !== -1) {
        // Calculate which page the filing is on
        const pageSize = paginationModel.pageSize;
        const targetPage = Math.floor(filingIndex / pageSize);
        
        // Navigate to that page
        setPaginationModel({ page: targetPage, pageSize });
        
        // Scroll to the filing after a delay to ensure pagination completes
        setTimeout(() => {
          const rowElement = document.querySelector(`[data-id="${highlightedFilingId}"]`);
          if (rowElement) {
            rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 500);
      }
    }
  }, [highlightedFilingId, filteredFilings]);
  useEffect(() => {
    fetchFilings();
  }, []);
  useEffect(() => {
    applyFilters();
  }, [filings, searchTerm, statusFilter, employeeFilter, frequencyFilter, dueByDate, viewMode, sentToClientFilter]);
  useEffect(() => {
    localStorage.setItem('fi_search', searchTerm);
    localStorage.setItem('fi_status', JSON.stringify(statusFilter));
    localStorage.setItem('fi_employee', JSON.stringify(employeeFilter));
    localStorage.setItem('fi_frequency', JSON.stringify(frequencyFilter));
    localStorage.setItem('fi_sent', sentToClientFilter);
  }, [searchTerm, statusFilter, employeeFilter, frequencyFilter, sentToClientFilter]);
// ADD THESE TWO useEffects:
useEffect(() => {
  setDynamicColumns(columns);
}, []);

useEffect(() => {
  const handleGlobalMouseDown = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    
    if (target.closest('.MuiDataGrid-columnHeader')) {
      const header = target.closest('.MuiDataGrid-columnHeader');
      if (header) {
        const rect = header.getBoundingClientRect();
        const isRightEdge = e.clientX > rect.right - 8;
        
        if (isRightEdge) {
          const fieldName = header.getAttribute('data-field');
          if (fieldName) {
            handleMouseDown(fieldName, e as any);
          }
        }
      }
    }
  };

  document.addEventListener('mousedown', handleGlobalMouseDown);
  
  return () => {
    document.removeEventListener('mousedown', handleGlobalMouseDown);
  };
}, [dynamicColumns]);
  const uniqueEmployees = Array.from(
    new Set(
      filings
        .map(f => f.client?.employeeAssigned)
        .filter(Boolean)
    )
  ).sort();
const handleMouseDown = (columnField: string, e: React.MouseEvent) => {
  e.preventDefault();
  const startX = e.clientX;
  let differenceX = 0;

  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';

  const originalColumn = dynamicColumns.find(col => col.field === columnField);
  const originalWidth = originalColumn?.width || 150;

  const handleMouseMove = (e: MouseEvent) => {
    differenceX = e.clientX - startX;
    const newWidth = Math.max(originalWidth + differenceX, 80);
    
    setDynamicColumns(prevColumns => 
      prevColumns.map(col => {
        if (col.field === columnField) {
          return { ...col, width: newWidth };
        }
        return col;
      })
    );
  };

  const handleMouseUp = () => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};  
const fetchFilings = async () => {
    try {
      setError(null);
      setLoading(true);
      const response = await filingApi.getAll();
      setFilings(response.data.data || []);
    } catch (error) {
      console.error('Error fetching filings:', error);
      setError('Failed to load filings');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...filings];
    
    // View Mode Filter (NEW)
    if (viewMode === 'needs-attention') {
      // Show only PENDING, WORK_IN_PROGRESS, OVERDUE with upcoming due dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      filtered = filtered.filter(f => {
        const dueDate = new Date(f.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        
        return (
          (f.status === FilingStatus.PENDING || 
          f.status === FilingStatus.WORK_IN_PROGRESS || 
          f.status === FilingStatus.OVERDUE) &&
          dueDate >= today
        );
      });
    }
    
    // Status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter(f => statusFilter.includes(f.status));
    }

    // Employee filter
    if (employeeFilter.length > 0) {
      filtered = filtered.filter(f => employeeFilter.includes(f.client?.employeeAssigned || ''));
    }

    // Frequency filter
    if (frequencyFilter.length > 0) {
      filtered = filtered.filter(f => frequencyFilter.includes(f.client?.hstFrequency || ''));
    }

    // Due by date filter
    if (dueByDate) {
      filtered = filtered.filter(f => {
        const dueDate = new Date(f.dueDate);
        return dueDate.getMonth() === dueByDate.getMonth() && 
              dueDate.getFullYear() === dueByDate.getFullYear();
      });
    }

    // Sent to client filter
    if (sentToClientFilter === 'yes') {
      filtered = filtered.filter(f => f.sentToClient === true);
    } else if (sentToClientFilter === 'no') {
      filtered = filtered.filter(f => f.sentToClient === false);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(filing =>
        filing.client?.clientName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by due date - LATEST FIRST (newest to oldest)
    filtered.sort((a, b) => {
      const dateA = new Date(a.dueDate).getTime();
      const dateB = new Date(b.dueDate).getTime();
      return dateB - dateA; // Descending order (newest first)
    });

    setFilteredFilings(filtered);
  };
  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter([]);
    setEmployeeFilter([]);
    setFrequencyFilter([]);
    setDueByDate(null);
    setViewMode('all');
    setSentToClientFilter('all');
    ['fi_search','fi_status','fi_employee','fi_frequency','fi_sent'].forEach(k => localStorage.removeItem(k));
  };
  const handleOpenStatusDialog = (filing: FilingRecord) => {
    setSelectedFiling(filing);
    setNewStatus(filing.status);
    setStatusDate(format(new Date(), 'yyyy-MM-dd'));
    setNotes(filing.notes || '');
    setStatusDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setSelectedFiling(null);
    setNotes('');
    setStatusDate('');
  };

  const handleUpdateStatus = async () => {
    if (!selectedFiling) return;

    try {
      await filingApi.updateStatus(selectedFiling.id, {
        status: newStatus,
        statusDate: statusDate,
        notes: notes || undefined,
      });

      handleCloseStatusDialog();
      fetchFilings();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const getStatusColor = (status: FilingStatus) => {
    switch (status) {
      case FilingStatus.FILED:
        return { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' };
      case FilingStatus.PENDING:
        return { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' };
      case FilingStatus.WORK_IN_PROGRESS:
        return { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' };
      case FilingStatus.RECEIVED:
        return { bg: '#E0E7FF', text: '#3730A3', border: '#A5B4FC' };
      case FilingStatus.OVERDUE:
        return { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' };
      case FilingStatus.INACTIVE:
        return { bg: '#F3F4F6', text: '#374151', border: '#D1D5DB' };
      default:
        return { bg: '#F3F4F6', text: '#374151', border: '#D1D5DB' };
    }
  };
  const columns: GridColDef[] = [
  {
    field: 'clientName',
    headerName: 'Client Name',
  width: 350,
    valueGetter: (params: any) => params.row.client?.clientName || 'N/A',
    renderCell: (params: any) => {
      const clientId = params.row.client?.id;
      const clientName = params.row.client?.clientName || 'N/A';
      
      if (!clientId) return clientName;
      
      return (
        <Box
          component="a"
          href={`/clients/${clientId}`}
          sx={{
            color: '#667eea',
            textDecoration: 'none',
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: '#764ba2',
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {clientName}
        </Box>
      );
    },
  },
{
    field: 'clientEmail',
    headerName: 'Client Email',
    width: 200,
    valueGetter: (params: any) => params.row.client?.clientEmail || 'N/A',
  },
{
    field: 'employee',
    headerName: 'Employee',
    width: 140,
    valueGetter: (params: any) => params.row.client?.employeeAssigned || 'N/A',
  },

{
      field: 'hstFrequency',
      headerName: 'Frequency',
      width: 110,  
      valueGetter: (params: any) => params.row.client?.hstFrequency || 'N/A',
    },
{
      field: 'status',
      headerName: 'Filing Status',
      width: 110,  
      renderCell: (params: any) => {
        const colors = getStatusColor(params.value);
        return (
          <Chip
            label={params.value}
            sx={{
              backgroundColor: colors.bg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
            }}
          />
        );
      },
    },
{
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params: any) => {
        const isFiled = params.row.status === FilingStatus.FILED;
        
        // Employees cannot modify filing status
        if (!canModifyFilings) {
          return (
            <Typography variant="caption" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>
              View Only
            </Typography>
          );
        }
        
        return isFiled ? (
          <Typography variant="caption" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>
            Status Locked
          </Typography>
        ) : (
          <Button
            size="small"
            onClick={() => handleOpenStatusDialog(params.row)}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#FFFFFF',
              fontWeight: 600,
              px: 2,
              '&:hover': { 
                background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)',
              },
            }}
          >
            Update Status
          </Button>
        );
      },
    },
{
      field: 'sentToClient',
      headerName: 'Sent to Client',
      width: 140,
      renderCell: (params: any) => {
        const SentCell = () => { const isSent = params.row.sentToClient; return (<select value={isSent ? "yes" : "no"} onChange={async (e) => { const val = e.target.value === "yes"; await filingApi.updateSentToClient(params.row.id, val); setFilings(prev => prev.map(f => f.id === params.row.id ? { ...f, sentToClient: val } : f)); }} style={{ cursor: "pointer", padding: "4px 8px", borderRadius: "999px", fontWeight: 600, fontSize: "0.75rem", border: isSent ? "1px solid #6EE7B7" : "1px solid #D1D5DB", backgroundColor: isSent ? "#D1FAE5" : "#F3F4F6", color: isSent ? "#065F46" : "#374151", outline: "none" }}><option value="yes">Yes</option><option value="no">No</option></select>); }; return <SentCell />;
      },
    },
{
      field: 'periodStartDate',
      headerName: 'Period Start',
      width: 120,
      renderCell: (params: any) => {
        try {
          const date = new Date(params.row.periodStartDate);
          return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' });
        } catch {
          return params.row.periodStartDate;
        }
      },
    },
{
      field: 'periodEndDate',
      headerName: 'Period End',
      width: 120,
      renderCell: (params: any) => {
        try {
          const date = new Date(params.row.periodEndDate);
          return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' });
        } catch {
          return params.row.periodEndDate;
        }
      },
    },
{
      field: 'dueDate',
      headerName: 'Due Date',
      width: 120,
      renderCell: (params: any) => {
        try {
          const date = new Date(params.row.dueDate);
          return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' });
        } catch {
          return params.row.dueDate;
        }
      },
    },
{
      field: 'statusDate',
      headerName: 'Filed Date',
      width: 120,
      renderCell: (params: any) => {
        // Get the most recent status date from history (we'll add this later)
        if (params.row.status === FilingStatus.FILED && params.row.filedDate) {
          try {
            const date = new Date(params.row.filedDate);
            return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' });
          } catch {
            return '-';
          }
        }
        return '-';
      },
    },
{
      field: 'lastNotified',
      headerName: 'Last Notified',
      width: 160, 
      renderCell: (params: any) => {
        const filing = params.row as FilingRecord;
        
        // Find the most recent notification sent
        const notifications = [
          { sent: filing.reminderOneMonthBeforeDue, label: '1mo before due' },
          { sent: filing.reminderTwoWeeksAfterPeriodEnd, label: '2wk after end' },
          { sent: filing.reminderOneDayAfterPeriodEnd, label: '1d after end' },
          { sent: filing.reminderOneWeekSent, label: '1wk before end' },
          { sent: filing.reminderTwoWeeksSent, label: '2wk before end' },
          { sent: filing.reminderOneMonthSent, label: '1mo before end' },
        ];

        const lastNotification = notifications.find(n => n.sent);
        
        if (!lastNotification) {
          return (
            <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.8125rem', fontStyle: 'italic' }}>
              None sent
            </Typography>
          );
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircle sx={{ color: '#10B981', fontSize: 14 }} />
            <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: '#374151', lineHeight: 1.2 }}>
              {lastNotification.label}
            </Typography>
          </Box>
        );
      },
    },
{
      field: 'nextNotification',
      headerName: 'Next Notification',
      width: 160, 
      renderCell: (params: any) => {
        const filing = params.row as FilingRecord;
        
        // If status is not PENDING, no notifications
        if (filing.status !== FilingStatus.PENDING) {
          return (
            <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.8125rem', fontStyle: 'italic' }}>
              N/A
            </Typography>
          );
        }
        const today = new Date();
        const periodEnd = new Date(filing.periodEndDate);
        const dueDate = new Date(filing.dueDate);
        
        // Calculate next notification based on what hasn't been sent yet
        const upcomingNotifications = [
          { 
            sent: filing.reminderOneMonthSent, 
            date: new Date(periodEnd.getTime() - 30 * 24 * 60 * 60 * 1000),
            label: '1mo before end'
          },
          { 
            sent: filing.reminderTwoWeeksSent, 
            date: new Date(periodEnd.getTime() - 14 * 24 * 60 * 60 * 1000),
            label: '2wk before end'
          },
          { 
            sent: filing.reminderOneWeekSent, 
            date: new Date(periodEnd.getTime() - 7 * 24 * 60 * 60 * 1000),
            label: '1wk before end'
          },
          { 
            sent: filing.reminderOneDayAfterPeriodEnd, 
            date: new Date(periodEnd.getTime() + 1 * 24 * 60 * 60 * 1000),
            label: '1d after end'
          },
          { 
            sent: filing.reminderTwoWeeksAfterPeriodEnd, 
            date: new Date(periodEnd.getTime() + 14 * 24 * 60 * 60 * 1000),
            label: '2wk after end'
          },
          { 
            sent: filing.reminderOneMonthBeforeDue, 
            date: new Date(dueDate.getTime() - 30 * 24 * 60 * 60 * 1000),
            label: '1mo before due'
          },
        ];

        // Find the next notification that hasn't been sent yet and is in the future
        const nextNotification = upcomingNotifications.find(n => !n.sent && n.date > today);

        if (!nextNotification) {
          return (
            <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.8125rem', fontStyle: 'italic' }}>
              All sent
            </Typography>
          );
        }

        const daysUntil = Math.ceil((nextNotification.date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 600, lineHeight: 1.2 }}>
              {nextNotification.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#6B7280', lineHeight: 1.2 }}>
              {daysUntil} {daysUntil === 1 ? 'day' : 'days'}
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '2rem', mb: 1, color: '#1a1a1a' }}>
            Filing Records
          </Typography>
          <Typography sx={{ color: '#666666', fontSize: '1rem' }}>
            Track and manage all tax filing submissions
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          onClick={fetchFilings}
          sx={{
            textTransform: 'none',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#FFFFFF',
            fontWeight: 600,
            px: 3,
            py: 1.25,
            '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)' },
          }}
        >
          Refresh
        </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Highlighted Filing Alert */}
        {highlightedFilingId && (
          <Alert 
            severity="info" 
            sx={{ mb: 3 }}
            onClose={() => {
              setHighlightedFilingId(null);
              // Remove filingId from URL
              navigate('/filings', { replace: true });
            }}
          >
            Showing filing from client details. Click the X to dismiss this highlight.
          </Alert>
        )}

        {/* Stats Cards */}
        <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          {[
            { label: 'Total Filings', count: filteredFilings.length, color: '#667eea', bg: '#EEF2FF', filter: null },
            { label: 'Pending', count: filteredFilings.filter(f => f.status === 'PENDING').length, color: '#F59E0B', bg: '#FEF3C7', filter: 'PENDING' },
            { label: 'Overdue', count: filteredFilings.filter(f => f.status === 'OVERDUE').length, color: '#EF4444', bg: '#FEE2E2', filter: 'OVERDUE' },
            { label: 'Work In Progress', count: filteredFilings.filter(f => f.status === 'WORK_IN_PROGRESS').length, color: '#3B82F6', bg: '#DBEAFE', filter: 'WORK_IN_PROGRESS' },
            { label: 'Filed', count: filteredFilings.filter(f => f.status === 'FILED').length, color: '#10B981', bg: '#D1FAE5', filter: 'FILED' },
          ].map((stat) => (
            <Box
              key={stat.label}
              onClick={() => stat.filter && setStatusFilter([stat.filter])}
              sx={{
                p: 2.5,
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                cursor: stat.filter ? 'pointer' : 'default',
                transition: 'all 0.2s',
                '&:hover': stat.filter ? { borderColor: stat.color, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } : {},
              }}
            >
              <Typography sx={{ fontSize: '0.8rem', color: '#6B7280', fontWeight: 600, mb: 0.5 }}>{stat.label}</Typography>
              <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: stat.color }}>{stat.count}</Typography>
            </Box>
          ))}
        </Box>

        {/* View Mode Toggle */}
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode !== null) {
                setViewMode(newMode);
              }
            }}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                border: '1px solid #E5E7EB',
                '&.Mui-selected': {
                  backgroundColor: '#667eea',
                  color: '#FFFFFF',
                  '&:hover': { backgroundColor: '#5568d3' },
                },
              },
            }}
          >
            <ToggleButton value="all">
              All Filings ({filings.length})
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Search and Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search Bar */}
        <TextField
          placeholder="Search by client name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: 300,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '& fieldset': { borderColor: '#E5E7EB' },
              '&:hover fieldset': { borderColor: '#667eea' },
              '&.Mui-focused fieldset': { borderColor: '#667eea' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#6B7280' }} />
              </InputAdornment>
            ),
          }}
        />
        {/* Status Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Filing Status</InputLabel>
          <Select
            label="Filing Status"
            multiple
            value={statusFilter}
            onChange={(e) => { const val = e.target.value as string[]; if (val.includes('all')) { setStatusFilter([]); } else { setStatusFilter(val); } }}
            renderValue={() => statusFilter.length === 0 ? 'All' : `${statusFilter.length} selected`}
            sx={{ fontWeight: 600 }}
          >
            <MenuItem value="all"><Checkbox checked={statusFilter.length === 0} /><ListItemText primary="All" /></MenuItem>
            <MenuItem value={FilingStatus.PENDING}><Checkbox checked={statusFilter.includes(FilingStatus.PENDING)} /><ListItemText primary="Pending" /></MenuItem>
            <MenuItem value={FilingStatus.WORK_IN_PROGRESS}><Checkbox checked={statusFilter.includes(FilingStatus.WORK_IN_PROGRESS)} /><ListItemText primary="Work In Progress" /></MenuItem>
            <MenuItem value={FilingStatus.RECEIVED}><Checkbox checked={statusFilter.includes(FilingStatus.RECEIVED)} /><ListItemText primary="Received" /></MenuItem>
            <MenuItem value={FilingStatus.FILED}><Checkbox checked={statusFilter.includes(FilingStatus.FILED)} /><ListItemText primary="Filed" /></MenuItem>
            <MenuItem value={FilingStatus.OVERDUE}><Checkbox checked={statusFilter.includes(FilingStatus.OVERDUE)} /><ListItemText primary="Overdue" /></MenuItem>
            <MenuItem value={FilingStatus.INACTIVE}><Checkbox checked={statusFilter.includes(FilingStatus.INACTIVE)} /><ListItemText primary="Inactive" /></MenuItem>
          </Select>
          {statusFilter.length > 0 && (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>{statusFilter.map(s => (<Chip key={s} label={s.replace(/_/g, ' ')} size="small" onDelete={() => setStatusFilter(statusFilter.filter(x => x !== s))} sx={{ fontSize: '0.7rem', height: 22, backgroundColor: '#EEF2FF', color: '#667eea', fontWeight: 600 }} />))}</Box>)}
        </FormControl>
        {/* Employee Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Employee</InputLabel>
          <Select
            label="Employee"
            multiple
            value={employeeFilter}
            onChange={(e) => { const val = e.target.value as string[]; if (val.includes('all')) { setEmployeeFilter([]); } else { setEmployeeFilter(val); } }}
            renderValue={() => employeeFilter.length === 0 ? 'All Employees' : `${employeeFilter.length} selected`}
            sx={{ fontWeight: 600 }}
          >
            <MenuItem value="all"><Checkbox checked={employeeFilter.length === 0} /><ListItemText primary="All Employees" /></MenuItem>
            {uniqueEmployees.map((emp) => (<MenuItem key={emp} value={emp}><Checkbox checked={employeeFilter.includes(emp as string)} /><ListItemText primary={emp} /></MenuItem>))}
          </Select>
          {employeeFilter.length > 0 && (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>{employeeFilter.map(e => (<Chip key={e} label={e} size="small" onDelete={() => setEmployeeFilter(employeeFilter.filter(x => x !== e))} sx={{ fontSize: '0.7rem', height: 22, backgroundColor: '#EEF2FF', color: '#667eea', fontWeight: 600 }} />))}</Box>)}
        </FormControl>

        {/* Frequency Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Frequency</InputLabel>
          <Select
            label="Frequency"
            multiple
            value={frequencyFilter}
            onChange={(e) => { const val = e.target.value as string[]; if (val.includes('all')) { setFrequencyFilter([]); } else { setFrequencyFilter(val); } }}
            renderValue={() => frequencyFilter.length === 0 ? 'All Frequencies' : `${frequencyFilter.length} selected`}
            sx={{ fontWeight: 600 }}
          >
            <MenuItem value="all"><Checkbox checked={frequencyFilter.length === 0} /><ListItemText primary="All Frequencies" /></MenuItem>
            <MenuItem value="MONTHLY"><Checkbox checked={frequencyFilter.includes('MONTHLY')} /><ListItemText primary="Monthly" /></MenuItem>
            <MenuItem value="QUARTERLY"><Checkbox checked={frequencyFilter.includes('QUARTERLY')} /><ListItemText primary="Quarterly" /></MenuItem>
            <MenuItem value="ANNUAL"><Checkbox checked={frequencyFilter.includes('ANNUAL')} /><ListItemText primary="Annual" /></MenuItem>
          </Select>
          {frequencyFilter.length > 0 && (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>{frequencyFilter.map(f => (<Chip key={f} label={f.charAt(0) + f.slice(1).toLowerCase()} size="small" onDelete={() => setFrequencyFilter(frequencyFilter.filter(x => x !== f))} sx={{ fontSize: '0.7rem', height: 22, backgroundColor: '#EEF2FF', color: '#667eea', fontWeight: 600 }} />))}</Box>)}
        </FormControl>

        {/* Sent To Client Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sent to Client</InputLabel>
          <Select
            label="Sent to Client"
            value={sentToClientFilter}
            onChange={(e) => setSentToClientFilter(e.target.value)}
            sx={{ fontWeight: 600 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        {sentToClientFilter !== 'all' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              <Chip
                label={`Sent: ${sentToClientFilter === 'yes' ? 'Yes' : 'No'}`}
                size="small"
                onDelete={() => setSentToClientFilter('all')}
                sx={{ fontSize: '0.7rem', height: 22, backgroundColor: '#EEF2FF', color: '#667eea', fontWeight: 600 }}
              />
            </Box>
          )}
        </FormControl>

        {/* Due By Date Filter */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600, fontSize: '0.875rem' }}>
            Due By:
          </Typography>
          <Box
            sx={{
              '& .react-datepicker-wrapper': { width: 'auto' },
              '& .react-datepicker__input-container input': {
                width: '200px',
                padding: '8px 14px',
                fontSize: '0.875rem',
                fontWeight: 600,
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                backgroundColor: dueByDate ? '#667eea' : '#FFFFFF',
                color: dueByDate ? '#FFFFFF' : '#374151',
                cursor: 'pointer',
                fontFamily: 'inherit',
                '&:hover': { borderColor: '#667eea' },
                '&:focus': { 
                  outline: 'none',
                  borderColor: '#667eea',
                  borderWidth: '2px',
                },
              },
              '& .react-datepicker': {
                fontFamily: 'inherit',
                border: '1px solid #E5E7EB',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              },
              '& .react-datepicker__header': {
                backgroundColor: '#F9FAFB',
                borderBottom: '1px solid #E5E7EB',
              },
              '& .react-datepicker__current-month': {
                color: '#667eea',
                fontWeight: 600,
              },
              '& .react-datepicker__month-wrapper': {
                display: 'flex',
                gap: '8px',
              },
              '& .react-datepicker__month-text': {
                padding: '8px',
                margin: '4px',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#667eea',
                  color: '#FFFFFF',
                },
              },
              '& .react-datepicker__month-text--keyboard-selected': {
                backgroundColor: '#667eea',
                color: '#FFFFFF',
              },
            }}
          >
            <DatePicker
              selected={dueByDate}
              onChange={(date: Date | null) => setDueByDate(date)}
              dateFormat="MMM yyyy"
              showMonthYearPicker
              placeholderText="Filter by Due Month"
              isClearable
            />
          </Box>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          {(searchTerm || statusFilter.length > 0 || employeeFilter.length > 0 || frequencyFilter.length > 0 || dueByDate || viewMode !== 'all' || sentToClientFilter !== 'all') && (
            <Button
              onClick={clearAllFilters}
              sx={{
                textTransform: 'none',
                color: '#EF4444',
                border: '1px solid #EF4444',
                fontWeight: 600,
                px: 2,
                py: 0.75,
                fontSize: '0.875rem',
                '&:hover': {
                  backgroundColor: '#FEF2F2',
                  borderColor: '#DC2626',
                },
              }}
            >
              Clear All Filters
            </Button>
          )}
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Showing {filteredFilings.length} of {filings.length} filings
          </Typography>
        </Box>
      </Box>

      {/* Bulk Action Toolbar */}
      {selectedRows.length > 0 && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#EEF2FF', border: '1px solid #667eea', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontWeight: 600, color: '#667eea' }}>{selectedRows.length} selected</Typography>
          <Button size="small" onClick={async () => {
            await Promise.all(selectedRows.map(id => filingApi.updateSentToClient(id, true)));
            fetchFilings(); setSelectedRows([]);
          }} sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontWeight: 600, px: 2 }}>
            Mark Sent ✓
          </Button>
          <Button size="small" onClick={async () => {
            await Promise.all(selectedRows.map(id => filingApi.updateSentToClient(id, false)));
            fetchFilings(); setSelectedRows([]);
          }} sx={{ textTransform: 'none', border: '1px solid #667eea', color: '#667eea', fontWeight: 600, px: 2 }}>
            Mark Not Sent
          </Button>
          <Button size="small" onClick={() => setSelectedRows([])} sx={{ textTransform: 'none', color: '#6B7280', ml: 'auto' }}>
            Deselect All
          </Button>
        </Box>
      )}

      {/* Data Grid */}
      <Box sx={{ width: '100%', backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
<DataGrid
  rows={filteredFilings}
  columns={dynamicColumns}
  loading={loading}
  pageSizeOptions={[10, 25, 50]}
  paginationModel={paginationModel}
  onPaginationModelChange={setPaginationModel}
  checkboxSelection
  onRowSelectionModelChange={(ids) => setSelectedRows(ids as string[])}
  rowSelectionModel={selectedRows}
  disableRowSelectionOnClick
  getRowClassName={(params) => 
    params.row.id === highlightedFilingId ? 'highlighted-row' : ''
  }
  sx={{
    border: 'none',
    '& .MuiDataGrid-cell': { borderBottom: '1px solid #F3F4F6', fontSize: '0.9375rem', color: '#374151' },
    '& .MuiDataGrid-columnHeaders': { 
      backgroundColor: '#F9FAFB', 
      borderBottom: '1px solid #E5E7EB', 
      fontSize: '0.875rem', 
      fontWeight: 600, 
      color: '#6B7280' 
    },
    '& .MuiDataGrid-columnHeader': {
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        cursor: 'col-resize',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: '#667eea',
        },
      },
    },
    '& .MuiDataGrid-row:hover': { backgroundColor: '#F9FAFB' },
    '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' },
    '& .highlighted-row': {
      backgroundColor: '#FEF3C7 !important',
      border: '2px solid #F59E0B !important',
      '& .MuiDataGrid-cell': {
        borderBottom: '1px solid #FCD34D !important',
      },
    },
  }}
/>
      </Box>

      {/* Update Status Dialog */}
      <Dialog open={statusDialogOpen} onClose={handleCloseStatusDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem', color: '#1a1a1a', pb: 2 }}>
          Update Filing Status
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#374151', fontWeight: 600, mb: 0.5 }}>
              Client: <strong>{selectedFiling?.client?.clientName}</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 4 }}>
            Period: {selectedFiling?.periodStartDate && new Date(selectedFiling.periodStartDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' })} -{' '}
            {selectedFiling?.periodEndDate && new Date(selectedFiling.periodEndDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' })}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                Status *
              </Typography>
              <TextField
                select
                fullWidth
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value as FilingStatus)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#667eea' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                  },
                }}
              >
                <MenuItem value={FilingStatus.PENDING}>Pending</MenuItem>
                <MenuItem value={FilingStatus.WORK_IN_PROGRESS}>Work In Progress</MenuItem>
                <MenuItem value={FilingStatus.RECEIVED}>Received</MenuItem>
                <MenuItem value={FilingStatus.FILED}>Filed</MenuItem>
                <MenuItem value={FilingStatus.OVERDUE}>Overdue</MenuItem>
              </TextField>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                  Status Date *
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  value={statusDate}
                  onChange={(e) => setStatusDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F9FAFB',
                      '& fieldset': { borderColor: '#E5E7EB' },
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                    },
                  }}
                />
                <Typography variant="caption" sx={{ color: '#6B7280', mt: 0.5, display: 'block' }}>
                  The actual date this status occurred
                </Typography>
              </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                Notes (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this filing..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#667eea' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleCloseStatusDialog}
            sx={{ textTransform: 'none', color: '#6B7280', fontWeight: 600, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            disabled={!statusDate}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#FFFFFF',
              fontWeight: 600,
              px: 4,
              '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)' },
              '&:disabled': { background: '#E5E7EB', color: '#9CA3AF' },
            }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Filings;
